import React, { useMemo, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Input, Table, TableProps } from 'antd';
import { WhiteWrapper, TableWrapper } from 'pages/SalesSettlement/styles';
import { useGetStoreClientFeesDetail, useMeInfo } from 'hooks';
import moment from 'moment';
import styled from 'styled-components';

const SearchInputWrapper = styled.div`
  flex: 1 1 280px;
  max-width: 360px;
  width: 100%;

  .ant-input-affix-wrapper.ant-input-affix-wrapper-lg {
    display: flex;
    align-items: center;
    height: 40px;
    max-height: 40px;
    padding: 0 11px;
    border-radius: 4px;
  }

  input:not([type='password']).ant-input.ant-input-lg {
    height: 35px;
    padding: 0;
    font-size: 14px;
    line-height: 38px;
  }

  input:not([type='password']).ant-input.ant-input-lg::placeholder {
    font-size: 14px;
    line-height: 38px;
  }

  .ant-input-prefix,
  .ant-input-suffix {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .ant-input-prefix {
    margin-right: 6px;
    color: #bfbfbf;
  }
`;

const columns: TableProps<any>['columns'] = [
  { title: '연번', dataIndex: 'idx', align: 'center', width: '5%' },
  { title: '고객사명', dataIndex: 'companyName', align: 'left', width: '34%' },
  {
    title: '거리(m)',
    dataIndex: 'distanceMeter',
    align: 'right',
    width: '7%',
    render: (_, values) => (values?.distanceMeter === -1 ? '-' : `${values.distanceMeter.toLocaleString()}`)
  },
  {
    title: '수수료 구분',
    dataIndex: 'paymentPolicy',
    align: 'center',
    width: '10%',
    render: (_, values) => {
      if (values?.paymentPolicy === 'RATE') {
        return '기본(%)';
      }
      if (values?.paymentPolicy === 'FIXED') {
        return '메뉴별 금액설정';
      }
      return '-';
    }
  },
  {
    title: '포인트 수수료(%)',
    dataIndex: 'pointCommissionRate',
    align: 'right',
    width: '10%',
    render: (_, values) => (values?.pointCommissionRate != null ? `${values?.pointCommissionRate?.toFixed(3)}` : '-')
  },
  {
    title: '대장페이 수수료(%)',
    dataIndex: 'captainPayCommissionRate',
    align: 'right',
    width: '10%',
    render: (_, values) => (values?.captainPayCommissionRate != null ? `${values?.captainPayCommissionRate?.toFixed(3)}` : '-')
  },
  { title: '비고', dataIndex: 'remark', align: 'left', width: '24%' }
];

const CompanyConnections = () => {
  const { selectedStore } = useMeInfo();
  const { data } = useGetStoreClientFeesDetail(selectedStore?.sid);
  const [searchKeyword, setSearchKeyword] = useState('');

  const revisedCompanyConnectionsInfo = useMemo(() => {
    if (!data) return [];

    return data.map((el, idx) => {
      const revisedIdx = idx + 1;

      const revisedRemarkFunc = ({ startDate, endDate }: { startDate?: string | null; endDate?: string | null }) => {
        if (startDate) return `노출예약 ${moment(startDate).format('YYYY-MM-DD')}`;
        if (endDate) return `비노출예약 ${moment(endDate).format('YYYY-MM-DD')}`;
        return '-';
      };

      const revisedRemark = revisedRemarkFunc({
        startDate: el.exposureStartDate,
        endDate: el.exposureEndDate
      });

      return {
        ...el,
        idx: revisedIdx,
        remark: revisedRemark
      };
    });
  }, [data]);

  const filteredCompanyConnectionsInfo = useMemo(() => {
    const normalizedKeyword = searchKeyword.trim().toLowerCase();

    if (!normalizedKeyword) return revisedCompanyConnectionsInfo;

    return revisedCompanyConnectionsInfo.filter((companyConnection) =>
      companyConnection.companyName?.toLowerCase().includes(normalizedKeyword)
    );
  }, [revisedCompanyConnectionsInfo, searchKeyword]);

  return (
    <div>
      <WhiteWrapper style={{ fontWeight: '700' }}>
        <div style={{ color: 'red' }}>수수료 요율은 부가가치세(VAT) 포함입니다.</div>
        <div>포인트 수수료: 회사에서 지급하는 포인트입니다.</div>
        <div>대장페이 수수료: 포인트 외 개인 결제로 충전한 금액입니다.</div>
        <div>공통 수수료는 계약서 관리의 내용을 참조해주세요.</div>
      </WhiteWrapper>

      <WhiteWrapper>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            paddingBottom: '8px'
          }}
        >
          <div style={{ fontWeight: '600' }}>총 {filteredCompanyConnectionsInfo.length} 개</div>
          <SearchInputWrapper>
            <Input
              size="large"
              prefix={<SearchOutlined />}
              placeholder="고객사명을 입력해주세요"
              allowClear
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
            />
          </SearchInputWrapper>
        </div>
        <div style={{ pointerEvents: 'none' }}>
          <TableWrapper>
            <Table
              columns={columns}
              dataSource={filteredCompanyConnectionsInfo}
              pagination={false}
              rowClassName={(record) => (record?.paymentPolicy === 'FIXED' ? 'table-row-fixed' : '')}
            />
          </TableWrapper>
        </div>
      </WhiteWrapper>
    </div>
  );
};

export default CompanyConnections;
